/**
 * Dynamic crosshair + hit marker.
 *
 * Four blades and a centre dot. The gap tracks the weapon's actual cone of fire:
 * it blooms when you move, jump or shoot and tightens the moment you stand still.
 * If the weapon system publishes a spread value we use it verbatim; otherwise we
 * run an equivalent model off player state so the crosshair is never wrong-looking
 * while the weapon module is still being built.
 *
 * The hit marker is the single most satisfying element on an FPS HUD, so it gets
 * a hand-driven animation curve rather than a CSS keyframe: a hard scale-in over
 * ~60 ms and a much slower fade, with a distinct shape for headshots and a red,
 * larger, louder variant for kills.
 */

export const TUNING = {
  baseGap: 3.2, // px, perfectly still and hip-fired
  gapPerUnit: 5.4, // px per unit of the internal spread model
  gapPerDegree: 8.5, // px per degree of real weapon spread, when available
  maxGap: 46, // airborne cones are genuinely this wide; don't lie about it
  ease: 16, // exponential convergence rate of the gap (1/s)
  writeEpsilon: 0.2, // px — below this we skip the DOM write entirely

  // fallback spread model (arbitrary units, tuned to feel like a 5.56 carbine)
  spreadIdle: 0.25,
  spreadMove: 0.85,
  spreadSprint: 1.7,
  spreadAir: 2.0,
  spreadCrouch: -0.2,
  bloomPerShot: 0.55,
  bloomMax: 3.2,
  bloomDecay: 5.0, // 1/s

  adsFade: 0.08, // s to fade the crosshair away when aiming

  hitDur: 0.4,
  hitKillDur: 0.6,
  hitScaleIn: 0.06,
};

const BLADES = [0, 90, 180, 270];

export class Crosshair {
  constructor(ctx, root) {
    this.ctx = ctx;

    // ---- blades ----------------------------------------------------------
    const el = document.createElement('div');
    el.className = 'xh hud-fade';
    const dot = document.createElement('i');
    dot.className = 'xh-dot';
    el.appendChild(dot);
    this.blades = BLADES.map((a) => {
      const b = document.createElement('i');
      b.className = 'xh-l';
      b.dataset.a = a;
      el.appendChild(b);
      return b;
    });
    root.appendChild(el);
    this.el = el;

    // ---- hit marker (separate root: it must survive the ADS fade) --------
    const hit = document.createElement('div');
    hit.className = 'xh-hit hud-fade';
    for (let i = 0; i < 4; i++) hit.appendChild(document.createElement('i'));
    root.appendChild(hit);
    this.hit = hit;
    this._hitBase = 'xh-hit hud-fade';

    // state
    this.gap = TUNING.baseGap;
    this.targetGap = TUNING.baseGap;
    this.bloom = 0;
    this.ads = false;
    this.opacity = 1;
    this.state = {
      sprinting: false,
      tacSprinting: false,
      crouching: false,
      grounded: true,
      sliding: false,
    };

    this._wroteGap = -999;
    this._wroteOpacity = -1;
    this._hitAge = -1;
    this._hitDur = 0;
    this._hitClass = '';
    this._hitScaleMul = 1;
    this._sawFireEvent = false;
    this._lastMag = -1;

    // Cached so the frame loop never touches window metrics.
    this._vh = window.innerHeight;
    this._onResize = () => { this._vh = window.innerHeight; };
    window.addEventListener('resize', this._onResize);

    this._bind();
  }

  _bind() {
    const ev = this.ctx.events;

    ev.on('player:state', (p) => {
      if (!p) return;
      this.state.sprinting = !!p.sprinting;
      this.state.tacSprinting = !!p.tacSprinting;
      this.state.crouching = !!p.crouching;
      this.state.grounded = p.grounded !== false;
      this.state.sliding = !!p.sliding;
      this.ads = !!p.ads;
    });

    ev.on('weapon:fire', () => {
      this._sawFireEvent = true;
      this.bloom = Math.min(TUNING.bloomMax, this.bloom + TUNING.bloomPerShot);
    });

    // Fallback shot detection while WeaponSystem is still a stub: a magazine
    // that went down by itself is a shot.
    ev.on('weapon:ammo', (p) => {
      if (!p) return;
      const mag = p.mag ?? 0;
      if (!this._sawFireEvent && this._lastMag >= 0 && mag < this._lastMag) {
        this.bloom = Math.min(TUNING.bloomMax, this.bloom + TUNING.bloomPerShot);
      }
      this._lastMag = mag;
    });

    ev.on('hit:actor', (p) => this.mark(p?.headshot ? 'hs' : 'body'));
    // `mine` and not merely "someone died": with teams in play a bot killing a
    // bot across the map must not flash a kill marker on your crosshair.
    ev.on('actor:death', (p) => {
      if (p?.mine) this.mark(p?.headshot ? 'killhs' : 'kill');
    });
  }

  dispose() {
    window.removeEventListener('resize', this._onResize);
  }

  /** kind: 'body' | 'hs' | 'kill' | 'killhs' */
  mark(kind) {
    const kill = kind === 'kill' || kind === 'killhs';
    const hs = kind === 'hs' || kind === 'killhs';
    let cls = this._hitBase;
    if (hs) cls += ' is-hs';
    if (kill) cls += ' is-kill';
    if (cls !== this._hitClass) {
      this.hit.className = cls;
      this._hitClass = cls;
    }
    this._hitAge = 0;
    this._hitDur = kill ? TUNING.hitKillDur : TUNING.hitDur;
    this._hitScaleMul = kill ? 1.45 : hs ? 1.12 : 1;

    this.ctx.events.emit('audio:play', {
      id: kill ? 'hitmarker_kill' : 'hitmarker',
      volume: kill ? 0.9 : 0.6,
      pitch: hs ? 1.18 : 1,
    });
  }

  /**
   * The weapon's true cone of fire, in screen pixels.
   *
   * Preference order: a spread value the weapon system publishes directly; then
   * the weapon's own spread table reconstructed against live player state — this
   * is the honest answer, because the gap then equals the actual half-angle of
   * the cone projected through the camera, so what you see is literally where
   * the bullets can go. Returns null if neither is available.
   */
  _realSpreadPx() {
    const w = this.ctx.weapons;
    if (!w) return null;
    const c = w.current;

    let cone = w.currentSpread ?? w.spread ?? c?.spread;
    if (typeof cone !== 'number' || !Number.isFinite(cone)) {
      const s = c?.def?.spread;
      if (!s || typeof s.hip !== 'number') return null;
      const p = this.ctx.player;
      const speed = p?.velocity ? Math.hypot(p.velocity.x, p.velocity.z) : 0;
      const moveT = Math.min(1, speed / 4.2);
      const st = this.state;
      if (this.ads) {
        cone = s.ads + (s.adsMoving - s.ads) * moveT;
        if (st.crouching) cone *= s.crouchScale ?? 1;
      } else if (st.sprinting || st.tacSprinting) {
        cone = s.hipSprint;
      } else {
        cone = s.hip + (s.hipMoving - s.hip) * moveT;
        if (st.crouching) cone *= s.crouchScale ?? 1;
      }
      if (!st.grounded) cone = Math.max(cone, s.hip) * (s.airScale ?? 1);
      cone += typeof c.bloom === 'number' ? c.bloom : 0;
    }

    // Project the half-angle through the camera. Half the viewport height maps
    // to tan(fov/2), so this is an exact pixel radius.
    const fov = this.ctx.camera?.fov;
    if (!fov) return (cone * 180 / Math.PI) * TUNING.gapPerDegree;
    return (Math.tan(cone) * (this._vh * 0.5)) / Math.tan((fov * Math.PI) / 360);
  }

  _modelSpread() {
    const s = this.state;
    let v = TUNING.spreadIdle;
    const p = this.ctx.player;
    let speed = 0;
    if (p?.velocity) speed = Math.hypot(p.velocity.x, p.velocity.z);
    if (speed > 0.6) v += TUNING.spreadMove * Math.min(1, speed / 5);
    if (s.sprinting || s.tacSprinting) v += TUNING.spreadSprint;
    if (!s.grounded) v += TUNING.spreadAir;
    if (s.crouching) v += TUNING.spreadCrouch;
    if (this.ads) v *= 0.35;
    return Math.max(0, v + this.bloom);
  }

  update(dt) {
    this.bloom *= Math.exp(-dt * TUNING.bloomDecay);
    if (this.bloom < 0.002) this.bloom = 0;

    const real = this._realSpreadPx();
    this.targetGap =
      real !== null
        ? TUNING.baseGap + real
        : TUNING.baseGap + this._modelSpread() * TUNING.gapPerUnit;
    if (this.targetGap > TUNING.maxGap) this.targetGap = TUNING.maxGap;

    const k = 1 - Math.exp(-dt * TUNING.ease);
    this.gap += (this.targetGap - this.gap) * k;

    // --- opacity (ADS fades the crosshair out completely) -----------------
    const wantOpacity = this.ads ? 0 : 1;
    if (this.opacity !== wantOpacity) {
      const step = dt / TUNING.adsFade;
      this.opacity =
        wantOpacity > this.opacity
          ? Math.min(wantOpacity, this.opacity + step)
          : Math.max(wantOpacity, this.opacity - step);
      const rounded = Math.round(this.opacity * 100) / 100;
      if (rounded !== this._wroteOpacity) {
        this.el.style.opacity = rounded;
        this._wroteOpacity = rounded;
      }
    }

    // --- blades: one transform write each, only when it moved -------------
    if (this.opacity > 0 && Math.abs(this.gap - this._wroteGap) > TUNING.writeEpsilon) {
      const d = (this.gap + 6).toFixed(1);
      const b = this.blades;
      b[0].style.transform = `rotate(0deg) translateY(-${d}px)`;
      b[1].style.transform = `rotate(90deg) translateY(-${d}px)`;
      b[2].style.transform = `rotate(180deg) translateY(-${d}px)`;
      b[3].style.transform = `rotate(270deg) translateY(-${d}px)`;
      this._wroteGap = this.gap;
    }

    // --- hit marker -------------------------------------------------------
    if (this._hitAge >= 0) {
      this._hitAge += dt;
      const age = this._hitAge;
      const dur = this._hitDur;
      if (age >= dur) {
        this._hitAge = -1;
        this.hit.style.opacity = '0';
      } else {
        const p = Math.min(1, age / TUNING.hitScaleIn);
        const inv = 1 - p;
        const eased = 1 - inv * inv * inv; // easeOutCubic
        const s = (0.5 + 0.5 * eased) * this._hitScaleMul * (1 + 0.1 * (1 - eased));
        const t = age / dur;
        const o = t < 0.3 ? 1 : 1 - (t - 0.3) / 0.7;
        this.hit.style.transform = `scale(${s.toFixed(3)})`;
        this.hit.style.opacity = o.toFixed(3);
      }
    }
  }
}
